import React from 'react';
import ReactMarkdown from 'react-markdown';

// 1. Detection Functions
function containsMarkdownTable(text: string): boolean {
  return /\|\s*.+?\s*\|.*\n\|\s*-+/.test(text);
}

function containsHTMLTable(text: string): boolean {
  return /<table[\s\S]*?>[\s\S]*?<\/table>/.test(text);
}

function containsTabularParagraph(text: string): boolean {
  return /<p[^>]*>\s*\|\s*.+?\|\s*.+?\|/i.test(text);
}

function isTabularGPTResponse(text: string): boolean {
  return (
    containsMarkdownTable(text) ||
    containsHTMLTable(text) ||
    containsTabularParagraph(text)
  );
}

// 2. Sanitize if fake-table found
function sanitizeTableLikeResponse(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?strong>/gi, '**')
    .replace(/^<p.*?>|<\/p>$/gi, '')
    .trim();
}

// 3. Component to render markdown safely
const GPTResponseRenderer = ({ content }: { content: string }) => {
  const sanitizedContent = isTabularGPTResponse(content)
    ? sanitizeTableLikeResponse(content)
    : content;

  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h1 className="text-2xl font-bold text-gray-800 my-3">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-xl font-semibold text-gray-800 my-2">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-medium text-gray-800 my-2">{children}</h3>
        ),
        p: ({ children }) => (
          <p className="my-2 text-gray-800 leading-relaxed">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-6 my-2 text-gray-800">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-6 my-2 text-gray-800">{children}</ol>
        ),
        li: ({ children }) => <li className="my-1">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 bg-gray-50 py-1 px-2 rounded">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-4 border-gray-300" />,
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {children}
          </a>
        ),
        img: ({ src = '', alt = '' }) => (
          <img src={src} alt={alt} className="my-3 rounded shadow max-w-full" />
        ),
        table: ({ children }) => (
          <table className="w-full border-collapse border border-gray-300 my-4 text-sm text-gray-800">
            {children}
          </table>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-100 font-medium">{children}</thead>
        ),
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => (
          <tr className="border-t border-gray-300">{children}</tr>
        ),
        th: ({ children }) => (
          <th className="px-3 py-2 text-left border border-gray-300 bg-gray-50">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td
            className="px-3 py-2 border border-gray-200 whitespace-pre-line"
            style={{ whiteSpace: 'pre-line' }}
          >
            {children}
          </td>
        ),
        code: ({ children }) => (
          <code
            style={{
              backgroundColor: 'transparent',
              padding: '0',
              fontSize: '13px',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              color: '#111827',
            }}
          >
            {children}
          </code>
        ),
        pre: ({ children }) => (
          <pre
            style={{
              backgroundColor: '#f3f3f357',
              color: '#1f2937',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '14px',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              overflowX: 'auto',
              marginTop: '0.5rem',
              marginBottom: '0.5rem',
            }}
          >
            {children}
          </pre>
        ),
      }}
    >
      {sanitizedContent}
    </ReactMarkdown>
  );
};

export default GPTResponseRenderer;
